package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.event.DragSession
import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic

/**
 * 拖拽排序装饰器：允许长按 body（VirtualList / VirtualGrid）内某项后拖到另一位置，松手重排。
 *
 * 字段：[enabled] 是否允许拖拽、[longPressMs] 长按进入拖拽的毫秒阈值、[onReorder] 重排回调（from,to）、
 * [dragHandleId] 仅在子元素该 id 处启动拖拽（null 时整行可拖）。
 */
class DragReorder private constructor() : ScrollDecorator<DragReorder>() {

    /** 重排回调：from→to 索引。 */
    fun interface ReorderCallback {
        fun onReorder(fromIndex: Int, toIndex: Int)
    }

    /** 排序拖拽专用 payload，避免与普通跨容器拖放的任意 payload 混淆。 */
    class ReorderPayload(@get:JvmName("reorderId") val reorderId: String, @get:JvmName("fromIndex") val fromIndex: Int)

    private var enabledFlag: Boolean = true
    private var longPressMsValue: Int = DEFAULT_LONG_PRESS_MS
    private var dragHandleIdValue: String? = null
    private var onReorderCallback: ReorderCallback? = null

    protected override fun self(): DragReorder = this

    // -------- 字段 --------

    fun enabled(e: Boolean): DragReorder {
        enabledFlag = e
        return this
    }

    override fun enabled(): Boolean = enabledFlag

    fun longPressMs(ms: Int): DragReorder {
        longPressMsValue = maxOf(0, ms)
        return this
    }

    fun longPressMs(): Int = longPressMsValue

    fun dragHandleId(id: String?): DragReorder {
        dragHandleIdValue = if (id.isNullOrEmpty()) null else id
        return this
    }

    fun dragHandleId(): String? = dragHandleIdValue

    /** 设置重排回调（IR 层把 DSL fun(from,to) 包成回调注入）。 */
    fun onReorder(callback: ReorderCallback?): DragReorder {
        onReorderCallback = callback
        return this
    }

    /**
     * 触发重排回调。Phase 4 的 DragSession 在松手命中目标项时调用；测试可直接驱动。
     * enabled=false 或 from==to 时忽略。
     */
    fun triggerReorder(fromIndex: Int, toIndex: Int) {
        if (!enabledFlag || fromIndex == toIndex) {
            return
        }
        onReorderCallback?.onReorder(fromIndex, toIndex)
    }

    /**
     * Phase 4 对接 DragSession：长按某项进入拖拽，以排序 payload 启动全局会话。enabled=false 时忽略。
     */
    fun startReorderDrag(fromIndex: Int, mx: Double, my: Double): DragSession? =
        startReorderDrag(fromIndex, null, mx, my)

    /** 长按某个具体行元素进入拖拽，ghost 使用该行元素而不是整个装饰器。 */
    fun startReorderDrag(fromIndex: Int, sourceElement: UIElement<*>?, mx: Double, my: Double): DragSession? {
        if (!enabledFlag) {
            return null
        }
        val sourceId = if (sourceElement != null) sourceElement.id() else id()
        return DragSession.start(sourceId.orEmpty(), sourceElement, ReorderPayload(id().orEmpty(), fromIndex), mx, my)
    }

    /**
     * Phase 4 对接 DragSession：松手命中目标项，从会话 payload 取 fromIndex，触发重排。
     * 会话不存在、payload 非本 DragReorder 或 from==to 时忽略。
     */
    fun handleDrop(toIndex: Int) {
        val session = DragSession.current() ?: return
        val payload = session.payload as? ReorderPayload ?: return
        val currentId = id() ?: return
        if (currentId == payload.reorderId) {
            triggerReorder(payload.fromIndex, toIndex)
        }
    }

    companion object {
        /** 长按进入拖拽的默认毫秒阈值。 */
        private const val DEFAULT_LONG_PRESS_MS = 300

        /** 创建一个拖拽排序装饰器。 */
        @JvmStatic
        fun create(): DragReorder = DragReorder()
    }
}
